import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

/**
 * Builds a flat list of attributes from test_steps with sequential letter refs.
 * Exported so AttributeTestingTableSubTab can reuse it.
 */
export function buildAttributeList(testSteps) {
  const result = [];
  let letterIndex = 0;
  (testSteps || []).forEach((step, stepIdx) => {
    (step.test_attributes || []).forEach((attr) => {
      result.push({
        ref:         String.fromCharCode(65 + letterIndex),
        label:       attr.label       || '',
        description: attr.description || '',
        key:         attr.key         || attr.label || '',
        procedure:   step._text       || step.summary || step.description || '',
        stepIndex:   stepIdx,
        attrIndex:   result.filter(a => a.stepIndex === stepIdx).length,
      });
      letterIndex++;
    });
  });
  return result;
}

/**
 * Sub-section 2 — Test Attributes (editable)
 *
 * Allows editing attribute label, description, and associated procedure.
 * Save calls back to ViewControl's handleSaveSteps which owns the API call.
 *
 * Props:
 *   testSteps        — editableSteps from ViewControl
 *   onStepChange     — (index, newStep) => void  — updates editableSteps in ViewControl
 *   handleSaveSteps  — async () => void
 *   stepsModified    — bool
 *   stepsSaving      — bool
 */
export default function TestAttributesSubTab({
  testSteps,
  onStepChange,
  handleSaveSteps,
  stepsModified,
  stepsSaving,
}) {
  const attributes = buildAttributeList(testSteps);

  if (attributes.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-sm text-gray-500">
          No test attributes defined yet. Add them in the Test Procedures tab.
        </p>
      </div>
    );
  }

  const handleAttrChange = (stepIdx, attrIdx, field, value) => {
    if (!onStepChange) return;
    const step = { ...(testSteps[stepIdx] || {}) };
    const attrs = [...(step.test_attributes || [])];
    attrs[attrIdx] = { ...attrs[attrIdx], [field]: value };
    step.test_attributes = attrs;
    onStepChange(stepIdx, step);
  };

  const handleProcedureChange = (stepIdx, value) => {
    if (!onStepChange) return;
    const step = { ...(testSteps[stepIdx] || {}), _text: value, summary: value };
    onStepChange(stepIdx, step);
  };

  return (
    <div>
      {/* Attribute table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              <th className="px-6 py-3 text-left w-20">Attr Ref</th>
              <th className="px-6 py-3 text-left w-72">Attribute Description</th>
              <th className="px-6 py-3 text-left">Test Procedures Performed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {attributes.map((attr, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors align-top">
                {/* Letter badge */}
                <td className="px-6 py-4">
                  <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                    {attr.ref}
                  </span>
                </td>

                {/* Editable label + description */}
                <td className="px-6 py-4">
                  <input
                    type="text"
                    value={attr.label}
                    onChange={e => handleAttrChange(attr.stepIndex, attr.attrIndex, 'label', e.target.value)}
                    placeholder="Attribute title"
                    className="w-full text-sm font-semibold text-gray-900 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent px-0 py-0.5 mb-1"
                  />
                  <textarea
                    rows={2}
                    value={attr.description}
                    onChange={e => handleAttrChange(attr.stepIndex, attr.attrIndex, 'description', e.target.value)}
                    placeholder="What is being checked…"
                    className="w-full text-xs text-gray-500 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none resize-none bg-transparent px-0 py-0.5"
                  />
                </td>

                {/* Editable procedure (parent test step) */}
                <td className="px-6 py-4">
                  <ProcedureEditor
                    value={attr.procedure}
                    onChange={v => handleProcedureChange(attr.stepIndex, v)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Renders procedure text as a numbered list.
 * Each line is independently editable; lines can be added/removed.
 */
function ProcedureEditor({ value, onChange }) {
  const lines = (value || '').split('\n').filter(Boolean);

  const updateLine = (idx, text) => {
    const next = [...lines];
    next[idx] = text;
    onChange(next.join('\n'));
  };
  const addLine = () => onChange([...lines, ''].join('\n'));
  const removeLine = (idx) => onChange(lines.filter((_, i) => i !== idx).join('\n'));

  return (
    <div className="space-y-1">
      {lines.map((line, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <span className="text-blue-600 text-xs font-medium mt-1.5 w-4 shrink-0">{idx + 1}.</span>
          <input
            type="text"
            value={line.replace(/^\d+\.\s*/, '')}
            onChange={e => updateLine(idx, e.target.value)}
            className="flex-1 text-sm text-gray-700 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent py-0.5"
          />
          <button onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-400 mt-1">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
      {lines.length === 0 && (
        <p className="text-xs text-gray-400 italic">No procedure text yet.</p>
      )}
      <button
        onClick={addLine}
        className="text-xs text-blue-500 hover:text-blue-700 inline-flex items-center gap-1 mt-1"
      >
        <Plus className="h-3 w-3" /> Add step
      </button>
    </div>
  );
}
